import { Browser } from "@capacitor/browser";
import { IonIcon, IonSkeletonText } from "@ionic/react";
import { chevronForwardOutline } from "ionicons/icons";
import React, { useCallback, useEffect, useState } from 'react';
import { useAnalytics } from "../../../AppAnalyticsProvider";
import { Announcement } from "../../../models/announcements";

interface RotatingBannerProps {
    initialTextArray: Announcement[];
    isLoadingAnnoucements: boolean;
    isError: boolean;
    onDismiss?: () => void;
}

const RotatingBanner: React.FC<RotatingBannerProps> = ({ initialTextArray, isLoadingAnnoucements, isError, onDismiss }) => {
    const { trackEvent } = useAnalytics();
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isSliding, setIsSliding] = useState(false);
    const [progress, setProgress] = useState(0);

    const DISPLAY_DURATION = 5000; // 5 seconds per announcement

    const dismissBanner = useCallback(() => {
        setIsSliding(true);
        setTimeout(() => {
            setIsVisible(false);
            onDismiss?.();
        }, 400);
    }, [onDismiss]);

    // Progress bar animation
    useEffect(() => {
        if (isLoadingAnnoucements || isError || initialTextArray.length === 0 || isSliding) {
            return;
        }

        setProgress(0);
        const startTime = Date.now();
        const animationFrame = () => {
            const elapsed = Date.now() - startTime;
            const newProgress = Math.min((elapsed / DISPLAY_DURATION) * 100, 100);
            setProgress(newProgress);
            if (newProgress < 100) {
                requestAnimationFrame(animationFrame);
            }
        };
        const frameId = requestAnimationFrame(animationFrame);
        return () => cancelAnimationFrame(frameId);
    }, [currentTextIndex, isLoadingAnnoucements, isError, initialTextArray.length, isSliding]);

    useEffect(() => {
        if (isLoadingAnnoucements || isError || initialTextArray.length === 0) {
            return;
        }

        // Single announcement - just stay visible, no need to cycle
        if (initialTextArray.length === 1) {
            return;
        }

        // Multiple announcements - keep cycling forever
        const interval = setInterval(() => {
            setCurrentTextIndex((prevIndex) =>
                prevIndex === initialTextArray.length - 1 ? 0 : prevIndex + 1
            );
        }, DISPLAY_DURATION);

        return () => clearInterval(interval);
    }, [initialTextArray, isLoadingAnnoucements, isError]);

    const handleLinkClick = async (url: string, announcement: Announcement) => {
        try {
            trackEvent('announcement_click', {
                announcement_id: announcement.id,
                announcement_text: announcement.text,
                announcement_url: url,
                position: currentTextIndex + 1,
                total_announcements: initialTextArray.length
            });

            await Browser.open({
                url,
                presentationStyle: 'popover'
            });
        } catch (error) {
            console.error('Error opening the browser', error);
        }
    };

    if (isError || !isVisible || initialTextArray.length === 0) {
        return null;
    }

    // Loading skeleton that matches the card style
    if (isLoadingAnnoucements) {
        return (
            <div className="flex flex-col justify-center items-center content-center">
                <div className="py-2 xs:w-full sm:w-10/12 md:w-7/12 lg:w-5/12 xl:w-6/12">
                    <div className="mx-2 rounded-2xl overflow-hidden bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900">
                        <div className="p-4">
                            <div className="flex items-center gap-3">
                                <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                                <IonSkeletonText animated style={{ width: '60%', height: '16px' }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentAnnouncement = initialTextArray[currentTextIndex];

    return (
        <div className="flex flex-col justify-center items-center content-center overflow-hidden">
            <div
                className={`py-2 xs:w-full sm:w-10/12 md:w-7/12 lg:w-5/12 xl:w-6/12 transition-all duration-400 ease-out ${isSliding
                        ? 'opacity-0 -translate-y-full scale-95'
                        : 'opacity-100 translate-y-0 scale-100'
                    }`}
                style={{
                    maxHeight: isSliding ? '0px' : '200px',
                    transitionProperty: 'transform, opacity, max-height'
                }}
            >
                {/* Card container - matches HomeTrickCard styling */}
                <div
                    className="mx-2 rounded-2xl overflow-hidden relative group cursor-pointer"
                    onClick={() => currentAnnouncement.link && handleLinkClick(currentAnnouncement.link, currentAnnouncement)}
                    style={{
                        background: 'linear-gradient(135deg, rgba(88, 97, 172, 0.15) 0%, rgba(50, 78, 128, 0.25) 50%, rgba(88, 97, 172, 0.15) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(88, 97, 172, 0.3)',
                        boxShadow: '0 4px 24px rgba(88, 97, 172, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
                    }}
                >
                    {/* Animated gradient border effect */}
                    <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{
                            background: 'linear-gradient(135deg, rgba(88, 97, 172, 0.3) 0%, rgba(143, 188, 143, 0.2) 50%, rgba(88, 97, 172, 0.3) 100%)',
                        }}
                    />

                    {/* Progress bar */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-zinc-800/50">
                        <div
                            className="h-full transition-all duration-100 ease-linear"
                            style={{
                                width: `${progress}%`,
                                background: 'linear-gradient(90deg, #5861AC, #8FBC8F, #5861AC)'
                            }}
                        />
                    </div>

                    {/* Content */}
                    <div className="relative p-4">
                        {/* Header row */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                {/* Logo */}
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
                                    style={{
                                        background: 'linear-gradient(135deg, #5861AC 0%, #324E80 100%)',
                                        boxShadow: '0 0 12px rgba(88, 97, 172, 0.5)'
                                    }}
                                >
                                    <img
                                        src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                        alt="Ecliptic"
                                        className="w-5 h-5 object-contain"
                                    />
                                </div>
                                <span
                                    className="text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: 'rgba(143, 188, 143, 0.9)' }}
                                >
                                    Ecliptic
                                </span>
                            </div>

                            {/* Pagination dots */}
                            {initialTextArray.length > 1 && (
                                <div className="flex gap-1.5">
                                    {initialTextArray.map((_, index) => (
                                        <div
                                            key={index}
                                            className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                                            style={{
                                                background: index === currentTextIndex
                                                    ? 'linear-gradient(135deg, #5861AC, #8FBC8F)'
                                                    : 'rgba(255,255,255,0.2)',
                                                transform: index === currentTextIndex ? 'scale(1.2)' : 'scale(1)',
                                                boxShadow: index === currentTextIndex
                                                    ? '0 0 8px rgba(88, 97, 172, 0.6)'
                                                    : 'none'
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Announcement text with slide animation */}
                        <div className="relative overflow-hidden" style={{ minHeight: '48px' }}>
                            {initialTextArray.map((announcement, index) => (
                                <div
                                    key={announcement.id || index}
                                    className={`transition-all duration-500 ease-out ${index === currentTextIndex
                                            ? 'opacity-100 translate-x-0 relative'
                                            : index < currentTextIndex
                                                ? 'opacity-0 -translate-x-full absolute inset-0'
                                                : 'opacity-0 translate-x-full absolute inset-0'
                                        }`}
                                >
                                    <p className="text-white text-base font-medium leading-relaxed">
                                        {announcement.text}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* CTA row */}
                        {currentAnnouncement.link && (
                            <div className="flex items-center justify-end mt-3 pt-3 border-t border-white/5">
                                <div
                                    className="flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all duration-300"
                                    style={{ color: '#8FBC8F' }}
                                >
                                    <span>Learn more</span>
                                    <IonIcon
                                        icon={chevronForwardOutline}
                                        className="text-base transition-transform duration-300 group-hover:translate-x-0.5"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Subtle animated shine effect */}
                    <div
                        className="absolute inset-0 pointer-events-none opacity-30"
                        style={{
                            background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.03) 55%, transparent 60%)',
                            animation: 'shine 3s ease-in-out infinite',
                        }}
                    />
                </div>
            </div>

            {/* Keyframe animation for shine effect */}
            <style>{`
                @keyframes shine {
                    0%, 100% { transform: translateX(-100%); }
                    50% { transform: translateX(100%); }
                }
            `}</style>
        </div>
    );
};

export default RotatingBanner;